import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DatePipe } from '@angular/common';
import { Router } from '@angular/router';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';

import { ProviderShell } from './provider-shell';
import { LanguageService } from '../../../core/i18n/language.service';
import { discoveryStateColor, discoveryStateKey } from './provider-discovery';
import { ProviderService } from '../services/provider.service';
import { ToastService } from '../../../shared/services/toast-service';
import { ModalService } from '../../../shared/components/modal/modal.service';
import { LabelBadge } from '../../../shared/components/label-badge/label-badge';

/**
 * Details tab body for every provider type: a read-only Provider card with what discovery
 * reports (name, service URL, discovery state and error, version, capabilities — all from the
 * provider's descriptor, so nothing here is editable), Delete provider, a Record card (incl. the
 * immutable type) and the "feeds by tenant" breakdown. Enabled is the only admin setting and
 * lives on the shell header's Enable/Disable button.
 *
 * Type-specific Details tabs wrap it and project extra left-column cards (e.g. SIRI's stop
 * health); the generic tree routes to it directly. Reads provider/feeds from the enclosing
 * `ProviderShell` rather than refetching.
 */
@Component({
  selector: 'app-provider-details-layout',
  standalone: true,
  imports: [TranslocoDirective, DatePipe, LabelBadge],
  templateUrl: './provider-details-layout.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  // See ProviderShellLayoutComponent's host binding — same inline-host fix; this
  // is the component whose sunken background was shrink-wrapping.
  host: { class: 'block h-full min-h-0' },
})
export class ProviderDetailsLayoutComponent {
  private readonly shell = inject(ProviderShell);
  private readonly languageService = inject(LanguageService);
  private readonly providerService = inject(ProviderService);
  private readonly toastService = inject(ToastService);
  private readonly modal = inject(ModalService);
  private readonly transloco = inject(TranslocoService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);

  readonly provider = this.shell.provider;
  readonly feedsByTenant = this.shell.feedsByTenant;
  readonly totalFeeds = this.shell.feeds;

  readonly discoveryDot = computed(() => discoveryStateColor(this.provider()?.discoveryState ?? null));
  readonly discoveryLabel = computed(() => {
    this.languageService.current(); // re-evaluate on language switch
    return this.transloco.translate(discoveryStateKey(this.provider()?.discoveryState ?? null));
  });
  /** The descriptor's file rules; null when the provider takes no files (or was never discovered). */
  readonly files = computed(() => this.provider()?.descriptor?.capabilities.files ?? null);

  async confirmDelete(): Promise<void> {
    const provider = this.provider();
    if (!provider) return;
    const confirmed = await this.modal.confirm({
      kind: 'destructive',
      destructive: true,
      title: this.transloco.translate('providers.detailPage.deleteProvider.title'),
      message: this.transloco.translate('providers.detailPage.deleteProvider.message', { name: provider.name }),
      confirmLabel: this.transloco.translate('providers.detailPage.deleteProvider.confirm'),
      confirmWord: provider.name,
    });
    if (!confirmed) return;
    this.providerService
      .delete(provider.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.toastService.show(this.transloco.translate('providers.toast.deleted'), 'success');
          void this.router.navigate(['/admin', 'providers']);
        },
        error: () => this.toastService.show(this.transloco.translate('providers.toast.deleteFailed'), 'error'),
      });
  }
}
